/**
 * @license Copyright (c) 2014-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor.js';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment.js';
import AutoImage from '@ckeditor/ckeditor5-image/src/autoimage.js';
import Autosave from '@ckeditor/ckeditor5-autosave/src/autosave.js';
import Autolink from '@ckeditor/ckeditor5-link/src/autolink.js';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold.js';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials.js';
import Heading from '@ckeditor/ckeditor5-heading/src/heading.js';
import Image from '@ckeditor/ckeditor5-image/src/image.js';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption.js';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize.js';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle.js';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar.js';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload.js';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic.js';
import Link from '@ckeditor/ckeditor5-link/src/link.js';
import List from '@ckeditor/ckeditor5-list/src/list.js';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed.js';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph.js';
import Table from '@ckeditor/ckeditor5-table/src/table.js';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar.js';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties.js';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties.js';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline.js';
import LinkImage from '@ckeditor/ckeditor5-link/src/linkimage.js';
import Mention from '@ckeditor/ckeditor5-mention/src/mention.js';
import CustomUploadAdapter from './custom_upload_adapter.js';
import PendingActions from '@ckeditor/ckeditor5-core/src/pendingactions.js';

let IFRAME_SRC = '//cdn.iframe.ly/api/iframe';
let API_KEY = '60833d9cafacfed719839d';

export default function MiiNewRichTextEditor() {

  // Plugins to include in the build.
  ClassicEditor.builtinPlugins = [
    Alignment,
    AutoImage,
    Autolink,
    Bold,
    Essentials,
    Heading,
    Image,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    Italic,
    Link,
    List,
    MediaEmbed,
    Paragraph,
    Table,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    Underline,
    LinkImage,
    Mention
  ];

  const editorClass = 'js-new-rich-text-editor';

  function shouldLoad() {
    return (document.body.getElementsByClassName(editorClass).length > 0)
  };

  function init() {
    if (shouldLoad()) {
      load();
    }
  };

  function load() {
    // console.log("Loading MiiRichTextEditor");
    const allEditors = document.querySelectorAll('.' + editorClass);
    allEditors.forEach(editor => {
      const uploadUrl = editor.getAttribute('data-upload-url');
      const mentionUrl = editor.getAttribute('data-mention-url');
      const saveUrl = editor.getAttribute('data-autosave-url');
      const fieldName = editor.getAttribute('name');
      const parsedFieldName = parseFieldName(fieldName);
      const modelName = parsedFieldName['model'];
      const attributeName = parsedFieldName['attribute'];

      ClassicEditor
        .create(editor, {
          extraPlugins: [ CustomUploadAdapter, Autosave, PendingActions, MentionCustomization],
          autosave: {
            save( editor ) {
              if(saveUrl != undefined) {
                return saveData( saveUrl, editor.getData(), modelName, attributeName );
              }
            }
          },
          toolbar: {
            items: [
              'bold',
              'italic',
              'underline',
              '|',
              'alignment',
              '|',
              'bulletedList',
              'numberedList',
              '|',
              'heading',
              '|',
              'link',
              '|',
              'imageUpload',
              'mediaEmbed',
              'insertTable'
            ]
          },
          mention: mentionUrl ? {
            feeds: [
              {
                marker: '@',
                minimumCharacters: 1,
                feed: async query => {
                  const response = await fetch(`${mentionUrl}?q=${encodeURIComponent(query)}`);
                  if (!response.ok) { return []; }
                  const data = await response.json();
                  return data.map(item => ({ id: '@' + item.name, userId: item.id, link: item.link, text: item.name }));
                }
              }
            ]
          } : undefined,
          heading: {
            options: [
              { model: 'paragraph', title: 'Normal', class: 'ck-heading_paragraph' },
              { model: 'heading4', view: 'h4', title: 'Titre', class: 'ck-heading_heading4' },
            ]
          },
          link: {
            addTargetToExternalLinks: true
          },
          image: {
            styles: {
              options: ['inline', 'block', 'alignRight', 'alignLeft']
            },

            // You need to configure the image toolbar, too, so it shows the new style
            // buttons as well as the resize buttons.
            toolbar: [
              'imageStyle:inline',
              'imageStyle:block',
              'imageStyle:alignLeft',
              'imageStyle:alignRight',
              '|',
              'toggleImageCaption',
              'imageTextAlternative',
              'linkImage'
            ]
          },
          table: {
            contentToolbar: [ 'tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties' ]
          },
          mediaEmbed: {
            // Previews are always enabled if there’s a provider for a URL (below regex catches all URLs)
            // By default `previewsInData` are disabled, but let’s set it to `false` explicitely to be sure
            previewsInData: false,
            providers: [
              {
                // hint: this is just for previews. Get actual HTML codes by making API calls from your CMS
                name: 'iframely previews',
                // Match all URLs or just the ones you need:
                url: /.+/,
                html: match => {
                  const url = match[ 0 ];
                  var iframeUrl = IFRAME_SRC + '?app=1&api_key=' + API_KEY + '&url=' + encodeURIComponent(url);
                  // alternatively, use &key= instead of &api_key with the MD5 hash of your api_key
                  // more about it: https://iframely.com/docs/allow-origins
                  return (
                    // If you need, set maxwidth and other styles for 'iframely-embed' class - it's yours to customize
                    '<div class="iframely-embed">' +
                    '<div class="iframely-responsive">' +
                    `<iframe src="${ iframeUrl }" ` +
                    'frameborder="0" allow="autoplay; encrypted-media" allowfullscreen>' +
                    '</iframe>' +
                    '</div>' +
                    '</div>'
                  );
                }
              }
            ]
          },
          customUpload: {
            // The URL that the images are uploaded to.
            uploadUrl: uploadUrl,
            // Enable the XMLHttpRequest.withCredentials property.
            // withCredentials: true,
            // Headers sent along with the XMLHttpRequest to the upload server.
            //     headers: {
            //         'X-CSRF-TOKEN': 'CSRF-Token',
            //         Authorization: 'Bearer <JSON Web Token>'
            //     }
          },
          language: 'fr'
        })
        .then( editor => {
          window.editor = editor;
          saveOnUnfocus( editor, saveUrl );
        })
        .catch( error => {
          console.log('Editor is error');
        } );
    })
  };

  function teardown() {
    const allEditors = document.querySelectorAll('.ck-editor__editable')
    console.log("teardown MiiRichTextEditor", allEditors);
    allEditors.forEach(editor => {
      editor.ckeditorInstance?.destroy();
    });
  };

  // Save the data to a fake HTTP server (emulated here with a setTimeout()).
  function saveData( url, data, modelName, attributeName) {
    return new Promise( resolve => {
      var formattedData = {[modelName]: {[attributeName]: data}};
      $.ajax({
        type: 'PUT',
        url: url,
        dataType: 'json',
        data: formattedData,
      }).done(function (data) {
        console.log('CKEditor content autosaved !')
        resolve();
      }).fail(function (xhr) {
        if (xhr.status === 422 && xhr.responseJSON && xhr.responseJSON.message) {
          alert(xhr.responseJSON.message);
        } else {
          console.log('CKEditor autosave failed =>' + msg);
        }
      });

    } );
  }

  // Listen to change:isFocused events from ckEditor and remove them from
  // if editor loses focus & there are pending actions it forced to save the editor content
  function saveOnUnfocus( editor, saveUrl ) {
    editor.editing.view.document.on( 'change:isFocused', ( evt, data, isFocused ) => {
      // console.log( `View document is focused: ${ isFocused }.` );
      if (isFocused == false) {
        const pendingActions = editor.plugins.get( 'PendingActions' );
        if (pendingActions.hasAny) {
          // console.log( `Unfocus => saving` );
          editor.config.get( 'autosave' ).save(editor);
          removeAllPendingActions(editor);
        }
      }
    } );
  }

  // Removes All pending actions
  function removeAllPendingActions(editor){
    var pendingActions = editor.plugins.get( 'PendingActions' );
    while (pendingActions.hasAny) {
      pendingActions.remove(pendingActions.first);
    }
  }

  // Parses the field Name and return an object detailing model & attribute
  // Ex: `description[collect_description]` => {model: 'description', attribute: 'collect_description'}
  function parseFieldName(fieldName) {
    const lBracketIndex = fieldName.indexOf("[");
    var attributeName = "";
    var modelName = "";
    if (lBracketIndex !== -1) {
      modelName = fieldName.substring(0, lBracketIndex);
      attributeName = fieldName.substring(lBracketIndex+1, fieldName.length-1);
    } else {
      attributeName = fieldName;
    }
    return {model: modelName, attribute: attributeName}
  }

  return {
    init: init,
    teardown: teardown
  };
};


function MentionCustomization( editor ) {
  editor.conversion.for( 'upcast' ).elementToAttribute( {
    view: {
      name: 'a',
      key: 'data-mention',
      classes: 'mention',
      attributes: {
        href: true,
        'data-user-id': true
      }
    },
    model: {
      key: 'mention',
      value: viewItem => {
        const mentionAttribute = editor.plugins.get( 'Mention' ).toMentionAttribute( viewItem, {
          link: viewItem.getAttribute( 'href' ),
          userId: viewItem.getAttribute( 'data-user-id' )
        } );

        return mentionAttribute;
      }
    },
    converterPriority: 'high'
  } );

  editor.conversion.for( 'downcast' ).attributeToElement( {
    model: 'mention',
    view: ( modelAttributeValue, { writer } ) => {
      if ( !modelAttributeValue ) {
        return;
      }

      return writer.createAttributeElement( 'a', {
        class: 'mention',
        'data-mention': modelAttributeValue.id,
        'data-mention-user-id': modelAttributeValue.userId,
        'href': modelAttributeValue.link,
        'target': '_blank'
      }, {
        priority: 20,
        id: modelAttributeValue.uid
      } );
    },
    converterPriority: 'high'
  } );
}
